import { useMemo } from "react";

const LEAF_VARIANTS = ["leaf-amber", "leaf-olive", "leaf-rust"];

/**
 * Atmospheric wind overlay for the hero section.
 *  - Two drifting fog/noise layers at different speeds (parallax)
 *  - A dense field of dust particles drifting horizontally
 *  - A handful of tumbling leaves in amber/olive/rust tones
 * All effects respect `prefers-reduced-motion`.
 */
export default function WindLayer() {
  // Stable particle field — generated once per mount.
  const particles = useMemo(
    () =>
      Array.from({ length: 32 }, (_, i) => ({
        id: i,
        top: Math.random() * 100,
        size: 1 + Math.random() * 2.2,
        delay: Math.random() * 30,
        duration: 14 + Math.random() * 18,
        opacity: 0.3 + Math.random() * 0.45,
      })),
    []
  );

  const leaves = useMemo(
    () =>
      Array.from({ length: 9 }, (_, i) => ({
        id: i,
        top: 6 + Math.random() * 86,
        size: 9 + Math.random() * 9,
        variant: LEAF_VARIANTS[i % LEAF_VARIANTS.length],
        delay: Math.random() * 35,
        duration: 22 + Math.random() * 22,
      })),
    []
  );

  return (
    <div className="absolute inset-0 overflow-hidden pointer-events-none" aria-hidden="true">
      {/* Fog layer 1 — slow, soft */}
      <div className="wind-fog wind-fog-1" />
      {/* Fog layer 2 — slower, lower */}
      <div className="wind-fog wind-fog-2" />
      {/* Dust particles */}
      <div className="absolute inset-0">
        {particles.map((p) => (
          <span
            key={p.id}
            className="dust"
            style={{
              top: `${p.top}%`,
              width: `${p.size}px`,
              height: `${p.size}px`,
              animationDelay: `-${p.delay}s`,
              animationDuration: `${p.duration}s`,
              opacity: p.opacity,
            }}
          />
        ))}
      </div>
      {/* Tumbling leaves */}
      <div className="absolute inset-0">
        {leaves.map((l) => (
          <span
            key={l.id}
            className={`leaf ${l.variant}`}
            style={{
              top: `${l.top}%`,
              width: `${l.size}px`,
              height: `${l.size}px`,
              animationDelay: `-${l.delay}s`,
              animationDuration: `${l.duration}s`,
            }}
          />
        ))}
      </div>
    </div>
  );
}
